import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  X, ArrowLeft, ArrowRight, CheckCircle, Coins, TrendUp, ChartPieSlice, Heart,
  CurrencyInr, ForkKnife, Heartbeat, Buildings, Pulse, ShoppingBag, Television,
  ShieldCheck, Scales, Rocket, Lightning, CalendarBlank, Eye, Sparkle,
} from "@phosphor-icons/react";

const slug = (v) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const STEPS = [
  {
    key: "goal", type: "single", q: "What's your primary goal?", help: "This helps us understand what success looks like for you.",
    options: [
      { v: "Steady monthly income", icon: Coins },
      { v: "Long-term wealth growth", icon: TrendUp },
      { v: "Diversify my portfolio", icon: ChartPieSlice },
      { v: "Own a piece of a brand I love", icon: Heart },
    ],
  },
  {
    key: "budget", type: "single", q: "How much are you looking to invest?", help: "An indicative range — nothing is committed here.",
    options: [
      { v: "₹5–10 Lakh", icon: CurrencyInr },
      { v: "₹10–25 Lakh", icon: CurrencyInr },
      { v: "₹25–50 Lakh", icon: CurrencyInr },
      { v: "₹50 Lakh+", icon: CurrencyInr },
    ],
  },
  {
    key: "sectors", type: "multi", q: "Which sectors interest you?", help: "Select all that apply.",
    options: [
      { v: "Food & Beverages", icon: ForkKnife },
      { v: "Wellness", icon: Heartbeat },
      { v: "Hospitality", icon: Buildings },
      { v: "Longevity / Health", icon: Pulse },
      { v: "Retail", icon: ShoppingBag },
      { v: "Appliances", icon: Television },
    ],
  },
  {
    key: "risk", type: "single", q: "What's your risk appetite?", help: "Returns and risk go hand in hand.",
    options: [
      { v: "Conservative (stable)", icon: ShieldCheck },
      { v: "Balanced", icon: Scales },
      { v: "Growth-focused (higher risk/reward)", icon: Rocket },
    ],
  },
  {
    key: "timeline", type: "single", q: "When do you want to start?", help: "No pressure — we'll pace it to you.",
    options: [
      { v: "Immediately", icon: Lightning },
      { v: "In 1–3 months", icon: CalendarBlank },
      { v: "Just exploring", icon: Eye },
    ],
  },
  { key: "contact", type: "contact", q: "Where should we send your matches?", help: "An advisor will curate opportunities and reach out." },
];

const TOTAL = STEPS.length;

export function QuestionnaireModal({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [answers, setAnswers] = useState({ sectors: [] });
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => { setStep(0); setDir(1); setAnswers({ sectors: [] }); setContact({ name: "", email: "", phone: "" }); setDone(false); };
  const close = () => { onClose(); setTimeout(reset, 400); };

  const go = (n) => { setDir(n > step ? 1 : -1); setStep(n); };
  const next = () => { if (step < TOTAL - 1) go(step + 1); };
  const back = () => { if (step > 0) go(step - 1); };

  const cur = STEPS[step];

  const choose = (s, value) => {
    if (s.type === "single") {
      setAnswers((a) => ({ ...a, [s.key]: value }));
      setTimeout(() => go(step + 1), 280);
    } else {
      setAnswers((a) => {
        const arr = a[s.key] || [];
        return { ...a, [s.key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
      });
    }
  };

  const submit = async () => {
    if (!contact.name || !contact.email || !contact.phone) return toast.error("Please fill in all your details");
    setSubmitting(true);
    try {
      await api.post("/quiz/submit", { ...answers, ...contact });
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong"); }
    finally { setSubmitting(false); }
  };

  const progress = done ? 100 : ((step + 1) / TOTAL) * 100;
  const variants = { enter: (d) => ({ opacity: 0, x: d > 0 ? 36 : -36 }), center: { opacity: 1, x: 0 }, exit: (d) => ({ opacity: 0, x: d > 0 ? -36 : 36 }) };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="quiz-fullscreen-overlay"
          initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[100] bg-navy-950/98 backdrop-blur-2xl overflow-y-auto">
          {/* ambient glow */}
          <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-gold/10 blur-[140px]" />

          {/* progress */}
          <div className="fixed top-0 left-0 right-0 h-1 bg-white/10 z-10">
            <motion.div className="h-full bg-gold" animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
          </div>

          {/* close */}
          <button onClick={close} data-testid="quiz-close-btn" className="fixed top-5 right-5 z-10 text-ebg-muted hover:text-gold transition-colors">
            <X size={26} />
          </button>
          {/* back */}
          {!done && step > 0 && (
            <button onClick={back} data-testid="quiz-back-btn" className="fixed top-5 left-5 z-10 text-ebg-muted hover:text-white flex items-center gap-1.5 text-sm transition-colors">
              <ArrowLeft size={18} /> Back
            </button>
          )}

          <div className="relative max-w-3xl mx-auto w-full min-h-screen flex flex-col justify-center pt-24 pb-16 px-6">
            {done ? (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="text-center" data-testid="quiz-done">
                <motion.div animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 3, repeat: Infinity }}
                  className="mx-auto grid place-items-center w-24 h-24 rounded-full gold-gradient text-navy-950 shadow-[0_0_60px_rgba(212,175,55,0.5)]">
                  <Sparkle size={40} weight="fill" />
                </motion.div>
                <h2 className="font-display text-4xl md:text-5xl text-white mt-8">Thank you. We'll curate your <span className="gold-text italic">perfect matches.</span></h2>
                <p className="text-ebg-muted mt-4 text-lg">An advisor will be in touch shortly with opportunities aligned to your goals.</p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center mt-10">
                  <a href="/explore" className="btn-gold rounded-full px-6 sm:px-8 py-3.5 font-semibold">Explore Franchises</a>
                  <button onClick={close} className="border border-white/20 text-white px-6 sm:px-8 py-3.5 rounded-full hover:border-gold hover:text-gold transition-colors">Done</button>
                </div>
              </motion.div>
            ) : (
              <>
                <p className="eyebrow mb-3">Question {step + 1} of {TOTAL}</p>
                <AnimatePresence mode="wait" custom={dir}>
                  <motion.div key={cur.key} custom={dir} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35 }}>
                    <h2 className="font-display text-3xl md:text-5xl text-white leading-tight">{cur.q}</h2>
                    <p className="text-ebg-muted mt-3 text-base md:text-lg">{cur.help}</p>

                    {cur.type === "contact" ? (
                      <div className="mt-10 space-y-4 max-w-lg">
                        <input data-testid="quiz-contact-name" placeholder="Full name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })}
                          className="w-full bg-white/5 border border-white/15 rounded-xl px-5 py-4 text-white placeholder:text-ebg-muted focus:border-gold focus:outline-none transition-colors" />
                        <input data-testid="quiz-contact-email" type="email" placeholder="Email address" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                          className="w-full bg-white/5 border border-white/15 rounded-xl px-5 py-4 text-white placeholder:text-ebg-muted focus:border-gold focus:outline-none transition-colors" />
                        <input data-testid="quiz-contact-phone" type="tel" placeholder="Mobile number" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                          className="w-full bg-white/5 border border-white/15 rounded-xl px-5 py-4 text-white placeholder:text-ebg-muted focus:border-gold focus:outline-none transition-colors" />
                        <button onClick={submit} disabled={submitting} data-testid="quiz-submit-btn"
                          className="btn-gold rounded-full px-8 py-4 font-semibold w-full disabled:opacity-50 mt-2 inline-flex items-center justify-center gap-2">
                          {submitting ? "Submitting…" : <>See My Matches <ArrowRight size={18} weight="bold" /></>}
                        </button>
                      </div>
                    ) : (
                      <div className={`mt-10 grid gap-4 ${cur.options.length > 4 ? "grid-cols-2 md:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
                        {cur.options.map((opt) => {
                          const selected = cur.type === "multi" ? (answers[cur.key] || []).includes(opt.v) : answers[cur.key] === opt.v;
                          return (
                            <button key={opt.v} onClick={() => choose(cur, opt.v)} data-testid={`quiz-option-card-${slug(opt.v)}`}
                              className={`relative flex items-center gap-4 rounded-xl p-5 md:p-6 text-left border transition-all hover:-translate-y-0.5 ${selected ? "border-gold bg-gold/10 ring-1 ring-gold shadow-[0_0_18px_rgba(212,175,55,0.18)]" : "border-white/15 bg-white/[0.03] hover:border-gold/50 hover:bg-gold/5"}`}>
                              <opt.icon size={28} weight="light" className={selected ? "text-gold" : "text-gold/70"} />
                              <span className="text-white text-sm md:text-base font-medium flex-1">{opt.v}</span>
                              {cur.type === "multi" && selected && <CheckCircle size={22} weight="fill" className="text-gold" />}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {cur.type === "multi" && (
                      <button onClick={next} data-testid="quiz-next-btn" disabled={(answers[cur.key] || []).length === 0}
                        className="btn-gold rounded-full px-8 py-3.5 font-semibold mt-10 inline-flex items-center gap-2 disabled:opacity-40">
                        Continue <ArrowRight size={18} weight="bold" />
                      </button>
                    )}
                  </motion.div>
                </AnimatePresence>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
